import { useTranslation } from 'react-i18next';
import { useScrollReveal } from '../../hooks/useScrollReveal';
import { SectionHeader } from '../ui/SectionHeader';
import styles from './TechStackSection.module.css';

// SVG Icons for each technology
const TECH_ICONS: Record<string, React.ReactNode> = {
  AWS: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" className={styles.svgIcon}>
      <path d="M4 15A4.5 4.5 0 0 1 3 6.1a9 9 0 0 1 17.8 2 4.5 4.5 0 0 1-.3 8.9Z" />
      <path d="M8.5 16.5c1.5 1 4 1.5 5.5 1.5s4-.5 5.5-1.5" stroke="var(--color-cyan)" strokeWidth="2" />
      <path d="M17 15.5l1.5 1.5-1.5 1.5" stroke="var(--color-cyan)" strokeWidth="2" />
    </svg>
  ),
  Lambda: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={styles.svgIcon}>
      <path d="M7 19l4-10m0 0l4 10m-4-10l-2-3" />
    </svg>
  ),
  DynamoDB: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" className={styles.svgIcon}>
      <ellipse cx="12" cy="5" rx="9" ry="3" />
      <path d="M3 5v6c0 1.66 4 3 9 3s9-1.34 9-3V5" />
      <path d="M3 11v6c0 1.66 4 3 9 3s9-1.34 9-3v-6" />
      <path d="M12 2v10" strokeDasharray="2 2" />
    </svg>
  ),
  CloudFront: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" className={styles.svgIcon}>
      <circle cx="12" cy="12" r="3" />
      <circle cx="5" cy="5" r="2" />
      <circle cx="19" cy="5" r="2" />
      <circle cx="19" cy="19" r="2" />
      <circle cx="5" cy="19" r="2" />
      <path d="M7 7l3.5 3.5M17 7l-3.5 3.5M17 17l-3.5-3.5M7 17l3.5-3.5" />
    </svg>
  ),
  S3: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" className={styles.svgIcon}>
      <rect x="3" y="3" width="18" height="18" rx="2" />
      <path d="M3 9h18M3 15h18M9 3v18M15 3v18" />
    </svg>
  ),
  React: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" className={styles.svgIcon}>
      <ellipse cx="12" cy="12" rx="10" ry="4" transform="rotate(30 12 12)" />
      <ellipse cx="12" cy="12" rx="10" ry="4" transform="rotate(90 12 12)" />
      <ellipse cx="12" cy="12" rx="10" ry="4" transform="rotate(150 12 12)" />
      <circle cx="12" cy="12" r="1.5" fill="currentColor" />
    </svg>
  ),
  TypeScript: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" className={styles.svgIcon}>
      <rect x="3" y="3" width="18" height="18" rx="2" />
      <path d="M7 9h3v6M12 14.5c.5.5 1 .8 1.5.8s1-.3 1-.8-.5-.8-1-1l-1-.5c-.8-.4-1.5-1-1.5-2s.8-2 2-2 2 .5 2.5.8" />
    </svg>
  ),
  Python: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" className={styles.svgIcon}>
      <path d="M12 2A4 4 0 0 0 8 6v2h4v1H6a4 4 0 0 0-4 4v2a4 4 0 0 0 4 4h2v-2a4 4 0 0 1 4-4h4v-1h6a4 4 0 0 0 4-4V6a4 4 0 0 0-4-4h-8z" />
      <circle cx="9.5" cy="5.5" r="0.5" fill="currentColor" />
      <circle cx="14.5" cy="18.5" r="0.5" fill="currentColor" />
    </svg>
  ),
  TensorFlow: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" className={styles.svgIcon}>
      <path d="M12 2L4 6v12l8 4 8-4V6l-8-4Z" />
      <path d="M12 2v20M4 6l8 4 8-4M4 18l8-4 8 4" />
    </svg>
  ),
  Docker: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" className={styles.svgIcon}>
      <path d="M2 12c4 0 6 3 10 3s6-3 10-3M5 12V8h3v4M9 12V8h3v4M13 12V8h3v4M6 8V5h3v3M10 8V5h3v3" />
      <circle cx="19" cy="10" r="1.5" />
    </svg>
  ),
  Kubernetes: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" className={styles.svgIcon}>
      <path d="M12 2L3 6v12l9 4 9-4V6l-9-4Z" />
      <path d="M12 6l-5 3v6l5 3 5-3V9l-5-3Z" />
      <path d="M12 2v4M3 6h4M3 18l4-2.5M12 22v-4M21 18l-4-2.5M21 6h-4" />
    </svg>
  ),
  MQTT: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" className={styles.svgIcon}>
      <circle cx="12" cy="12" r="2.5" />
      <path d="M16.24 7.76a6 6 0 0 1 0 8.49M7.76 7.76a6 6 0 0 0 0 8.49M19.07 4.93a10 10 0 0 1 0 14.14M4.93 4.93a10 10 0 0 0 0 14.14" />
    </svg>
  ),
  CDK: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" className={styles.svgIcon}>
      <path d="M16 16v1a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2h11a2 2 0 0 1 2 2v1" />
      <path d="M18 8h3a2 2 0 0 1 2 2v7a2 2 0 0 1-2 2h-9a2 2 0 0 1-2-2" />
      <path d="M6 10l-2 2 2 2M10 10l2 2-2 2" />
    </svg>
  ),
  Grafana: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" className={styles.svgIcon}>
      <path d="M3 3v18h18" />
      <path d="M18.7 8l-5.1 5.2-2.8-2.7L7 14.3" />
      <circle cx="7" cy="14" r="1.25" />
      <circle cx="11" cy="10" r="1.25" />
      <circle cx="13.5" cy="13.5" r="1.25" />
      <circle cx="18.5" cy="8" r="1.25" />
    </svg>
  ),
};

// Category SVG Icons
const CATEGORY_ICONS: Record<string, React.ReactNode> = {
  iot_edge: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <rect x="4" y="4" width="16" height="16" rx="2" />
      <circle cx="9" cy="9" r="1" />
      <circle cx="15" cy="9" r="1" />
      <circle cx="9" cy="15" r="1" />
      <circle cx="15" cy="15" r="1" />
      <path d="M9 9h6v6H9z" strokeDasharray="3 3" />
      <path d="M12 2v2M12 20v2M2 12h2M20 12h2" />
    </svg>
  ),
  cloud_serverless: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17.5 19A4.5 4.5 0 0 0 21 11.5a6 6 0 0 0-11.4-3A4.5 4.5 0 0 0 3 13.5 4.5 4.5 0 0 0 7.5 18" />
      <path d="M12 10l-3 4.5h4L11 19.5" stroke="var(--color-cyan)" strokeWidth="2" />
    </svg>
  ),
  data_ai: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 2A10 10 0 0 0 2 12a10 10 0 0 0 10 10v-5a5 5 0 0 1-5-5 5 5 0 0 1 5-5V2z" />
      <path d="M12 2a10 10 0 0 1 10 10 10 10 0 0 1-10 10" />
      <circle cx="16.5" cy="8.5" r="1" />
      <circle cx="18.5" cy="12.5" r="1" />
      <circle cx="16.5" cy="16.5" r="1" />
      <path d="M12 8.5h3.5M12 12.5h5.5M12 16.5h3.5" />
    </svg>
  ),
  web_interfaces: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="3" width="20" height="18" rx="2" />
      <path d="M2 8h20M6 5.5h.01M9 5.5h.01M12 5.5h.01" />
      <path d="M6 13l2.5 2.5L6 18M11 17h5" />
    </svg>
  ),
};

const FLEXIBILITY_ICON = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 7H11M14 17H4M10 12h10M4 12h2" />
    <circle cx="8" cy="7" r="2.5" />
    <circle cx="16" cy="17" r="2.5" />
    <circle cx="7" cy="12" r="2.5" />
  </svg>
);

interface TechCategoryData {
  id: string;
  translationKey: string;
  items: string[];
}

const CATEGORIES: TechCategoryData[] = [
  {
    id: 'iot_edge',
    translationKey: 'iot_edge',
    items: ['MQTT', 'Docker', 'Python'],
  },
  {
    id: 'cloud_serverless',
    translationKey: 'cloud_serverless',
    items: ['AWS', 'Lambda', 'S3', 'CloudFront', 'CDK'],
  },
  {
    id: 'data_ai',
    translationKey: 'data_ai',
    items: ['DynamoDB', 'TensorFlow', 'Python', 'Grafana'],
  },
  {
    id: 'web_interfaces',
    translationKey: 'web_interfaces',
    items: ['React', 'TypeScript'],
  },
];

export const TechStackSection = () => {
  const { t } = useTranslation();
  const [ref, visible] = useScrollReveal(0.12);

  return (
    <section id="tech-stack" ref={ref as React.RefObject<HTMLElement>} className={styles.section}>
      <div className="container">
        <SectionHeader
          label={t('techStack.label')}
          title={t('techStack.title')}
          subtitle={t('techStack.subtitle')}
        />

        <div className={`${styles.mainLayout} ${visible ? styles.visible : ''}`}>
          {/* Left Column: Flexibility Note */}
          <div className={styles.flexibilityColumn}>
            <div className={styles.flexibilityCard}>
              <div className={styles.flexibilityIconContainer}>
                {FLEXIBILITY_ICON}
              </div>
              <div className={styles.flexibilityContent}>
                <div className={styles.flexibilityBadge}>
                  <span className={styles.badgePulse} />
                  SYSTEM INTEROPERABILITY
                </div>
                <h3 className={styles.flexibilityTitle}>
                  {t('techStack.flexibility_title')}
                </h3>
                <p className={styles.flexibilityDesc}>
                  {t('techStack.flexibility_desc')}
                </p>
              </div>
              <div className={styles.cardGradientOverlay} />
            </div>
          </div>

          {/* Right Column: Categorized Cards Grid */}
          <div className={styles.gridColumn}>
            {CATEGORIES.map((category, idx) => (
              <div
                key={category.id}
                className={styles.categoryCard}
                style={{ transitionDelay: `${idx * 100}ms` }}
              >
                <div className={styles.cardHeader}>
                  <div className={styles.categoryIcon}>
                    {CATEGORY_ICONS[category.id]}
                  </div>
                  <div>
                    <h3 className={styles.categoryTitle}>
                      {t(`techStack.categories.${category.translationKey}.title`)}
                    </h3>
                    <p className={styles.categoryDesc}>
                      {t(`techStack.categories.${category.translationKey}.desc`)}
                    </p>
                  </div>
                </div>

                <div className={styles.techList}>
                  {category.items.map((tech) => (
                    <div key={tech} className={styles.techPill} title={tech}>
                      <span className={styles.techPillIcon}>
                        {TECH_ICONS[tech] || (
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <circle cx="12" cy="12" r="10" />
                          </svg>
                        )}
                      </span>
                      <span className={styles.techPillName}>{tech}</span>
                    </div>
                  ))}
                </div>

                {/* Cyber decorative grid intersection indicator */}
                <div className={styles.cornerIndicator} />
                <div className={styles.cardGradientOverlay} />
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
